<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import { switchGroup } from '@/services/session'
import { useUiStore } from '@/stores/ui'
import { useUserStore } from '@/stores/user'

/**
 * Active-group selector.
 *
 * Switching groups can change the user's role and therefore the whole menu, so
 * `switchGroup` reloads it and tells us whether the page we are on is still
 * reachable — if not, we fall back to the dashboard, as the spec requires.
 */
const { t } = useI18n()
const user = useUserStore()
const ui = useUiStore()
const router = useRouter()
const route = useRoute()

const switching = ref(false)

// I gruppi su un mondo concluso spariscono da qui: torneranno in un archivio
// gruppi dedicato, non ancora costruito. La membership resta intatta in DB.
const groups = computed(() => user.groups.filter((group) => !group.server?.closed))
const selected = computed(() => user.selectedGroup)
const hasChoice = computed(() => groups.value.length > 1)

async function choose(groupId: number): Promise<void> {
  if (switching.value || selected.value?.id === groupId) return

  switching.value = true
  try {
    const { stillAllowed } = await switchGroup(groupId, route.path)

    const group = user.groups.find((candidate) => candidate.id === groupId)
    if (group) ui.notifySuccess(t('header.group.switched', { name: group.name }))

    if (!stillAllowed) await router.push({ name: 'home' })
  } catch {
    // The HTTP layer already surfaced the error as a toast.
  } finally {
    switching.value = false
  }
}

function goToManageGroups(): void {
  void router.push({ name: 'groups-list' })
}

function goToGroupSettings(): void {
  void router.push({ name: 'group-settings' })
}
</script>

<template>
  <!-- No group at all: nothing to choose from. -->
  <div
    v-if="groups.length === 0"
    class="text-parchment-300 flex min-w-0 items-center gap-2 rounded-md border border-white/10 px-2.5 py-1.5 text-sm"
  >
    <AppIcon name="mdi-shield-off-outline" class="shrink-0 text-base opacity-70" />
    <span class="max-w-32 truncate">{{ t('header.group.none') }}</span>
  </div>

  <!-- Exactly one group: read-only, per the spec. -->
  <div
    v-else-if="!hasChoice"
    class="text-parchment-100 flex min-w-0 items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2.5 py-1.5 text-sm"
    :title="t('header.group.readonly')"
  >
    <AppIcon name="mdi-shield-crown-outline" class="shrink-0 text-gold-300 text-base" />
    <span class="max-w-32 truncate font-semibold">{{ selected?.name ?? groups[0]?.name }}</span>
    <RouterLink
      v-if="selected?.role.admin"
      :to="{ name: 'group-settings' }"
      class="text-parchment-300 hover:text-gold-300 shrink-0"
      :aria-label="t('common.groupSettings')"
      :title="t('common.groupSettings')"
    >
      <AppIcon name="mdi-shield-cog-outline" class="text-base" />
    </RouterLink>
  </div>

  <DropdownMenuRoot v-else>
    <DropdownMenuTrigger
      class="chrome-item min-w-0 cursor-pointer gap-2 border border-white/10 bg-black/20 px-2.5"
      :disabled="switching"
      :aria-label="t('common.fields.group')"
    >
      <AppIcon
        :name="switching ? 'mdi-loading' : 'mdi-shield-crown-outline'"
        class="shrink-0 text-gold-300 text-base"
        :class="{ 'animate-spin': switching }"
      />
      <span class="max-w-32 truncate font-semibold">
        {{ selected?.name ?? t('header.group.none') }}
      </span>
      <AppIcon name="mdi-chevron-down" class="text-base opacity-70" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        :side-offset="10"
        align="end"
        class="panel z-50 max-h-80 min-w-56 overflow-y-auto p-1 shadow-raised"
      >
        <DropdownMenuLabel class="text-abyss-800/70 px-2 py-1 text-xs font-semibold uppercase">
          {{ t('common.fields.group') }}
        </DropdownMenuLabel>
        <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />

        <DropdownMenuItem
          v-for="group in groups"
          :key="group.id"
          class="text-abyss-900 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-highlighted:bg-primary-50"
          @select="choose(group.id)"
        >
          <span class="flex-1 truncate">{{ group.name }}</span>
          <AppIcon v-if="group.role.selected" name="mdi-check" class="text-primary-600 shrink-0" />
        </DropdownMenuItem>

        <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />
        <DropdownMenuItem
          v-if="selected?.role.admin"
          class="text-primary-800 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold outline-none data-highlighted:bg-primary-50"
          @select="goToGroupSettings"
        >
          <AppIcon name="mdi-shield-cog-outline" />
          <span>{{ t('common.groupSettings') }}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          class="text-primary-800 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold outline-none data-highlighted:bg-primary-50"
          @select="goToManageGroups"
        >
          <AppIcon name="mdi-cog-outline" />
          <span>{{ t('header.group.manage') }}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
